import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Award, Users, Clock, Target } from 'lucide-react';

const AboutPage = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();

  useEffect(() => {
    document.title = currentLang === 'ar'
      ? 'من نحن | أرمان كيتشنز'
      : 'About Us | Arman Kitchens';
  }, [currentLang]);

  const values = [
    { icon: Award, title: currentLang === 'ar' ? 'الجودة' : 'Quality', desc: currentLang === 'ar' ? 'نلتزم بأعلى معايير الجودة في كل مشروع' : 'We commit to the highest quality standards in every project' },
    { icon: Users, title: currentLang === 'ar' ? 'العملاء أولاً' : 'Customers First', desc: currentLang === 'ar' ? 'رضا عملائنا هو أولويتنا القصوى' : 'Customer satisfaction is our top priority' },
    { icon: Clock, title: currentLang === 'ar' ? 'الالتزام بالوقت' : 'Timely Delivery', desc: currentLang === 'ar' ? 'نلتزم بالمواعيد المحددة للتسليم' : 'We commit to delivering on schedule' },
    { icon: Target, title: currentLang === 'ar' ? 'الدقة' : 'Precision', desc: currentLang === 'ar' ? 'الاهتمام بأدق التفاصيل في التصميم والتنفيذ' : 'Attention to the finest details in design and execution' },
  ];

  return (
    <main data-testid="about-page" className="pt-24">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1527501034545-bbadafbf308b)' }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-text mb-4 block">{t('about.label')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-3xl">
              {t('about.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {currentLang === 'ar' ? 'قصتنا' : 'Our Story'}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {currentLang === 'ar' 
                  ? 'منذ أكثر من عشر سنوات، بدأت أرمان كيتشنز رحلتها في عالم تصميم وتصنيع المطابخ الفاخرة في المملكة العربية السعودية. نحن نؤمن بأن المطبخ هو قلب المنزل، ونسعى لجعله مساحة تجمع بين الجمال والوظيفة.'
                  : 'For over ten years, Arman Kitchens has been on a journey in the world of luxury kitchen design and manufacturing in Saudi Arabia. We believe that the kitchen is the heart of the home, and we strive to make it a space that combines beauty and functionality.'}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {currentLang === 'ar'
                  ? 'فريقنا من المصممين والحرفيين المهرة يعمل بشغف لتحويل رؤيتك إلى واقع. نستخدم أجود المواد وأحدث التقنيات لنضمن لك مطبخاً يدوم لسنوات طويلة.'
                  : 'Our team of skilled designers and craftsmen work passionately to transform your vision into reality. We use the finest materials and latest technologies to ensure you get a kitchen that lasts for years.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1650615653338-0ec058c99fa1"
                  alt="Craftsmanship"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 start-8 bg-gold p-6 text-charcoal">
                <span className="text-4xl font-bold">10+</span>
                <span className="block text-sm mt-1">
                  {currentLang === 'ar' ? 'سنوات من الخبرة' : 'Years of Experience'}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-secondary grain">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="label-text mb-4 block">
              {currentLang === 'ar' ? 'قيمنا' : 'Our Values'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              {currentLang === 'ar' ? 'ما يميزنا' : 'What Sets Us Apart'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 bg-background"
              >
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-gold" size={28} />
                </div>
                <h3 className="font-semibold text-foreground text-lg mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-brand-dark text-white grain">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="label-text mb-4 block">
              {currentLang === 'ar' ? 'فريقنا' : 'Our Team'}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {currentLang === 'ar' ? 'فريق من المحترفين' : 'A Team of Professionals'}
            </h2>
            <p className="text-white/60 text-lg">
              {currentLang === 'ar'
                ? 'يضم فريقنا مصممين ومهندسين وحرفيين ذوي خبرة واسعة في مجال تصميم وتصنيع المطابخ الفاخرة.'
                : 'Our team includes experienced designers, engineers, and craftsmen with extensive experience in luxury kitchen design and manufacturing.'}
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
